/**
 * TERABIZZ components. Small, composable, token-driven. Import from "@/terabizz".
 * Buttons, inputs, selects, dialogs and drawers come from the underlying library, re-skinned by terabizz.css.
 */
import { ArrowDownMini, ArrowUpMini, ExclamationCircle, InformationCircle, CheckCircle, XMark } from "@medusajs/icons"
import { Skeleton } from "@medusajs/ui"
import type { ReactNode } from "react"
import { Link } from "react-router-dom"

export type Tone = "neutral" | "info" | "success" | "attention" | "warning" | "critical" | "magic"

const toneVars = (t: Tone) => ({ background: `var(--tb-${t}-bg)`, color: `var(--tb-${t}-fg)` })

/** Status pill. `progress` draws the Shopify-style ring/half/filled dot for fulfilment-like states. */
export function Badge({ tone = "neutral", dot, children }: { tone?: Tone; dot?: "ring" | "filled"; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-x-1.5 whitespace-nowrap rounded-[var(--k-r-sm)] px-2 py-[2px] text-[13px] font-medium leading-4" style={toneVars(tone)}>
      {dot && <span aria-hidden className="inline-block h-[7px] w-[7px] rounded-full" style={dot === "filled" ? { background: "currentColor" } : { border: "1.5px solid currentColor" }} />}
      {children}
    </span>
  )
}

const BANNER_ICON: Record<Tone, ReactNode> = {
  neutral: <InformationCircle />,
  info: <InformationCircle />,
  success: <CheckCircle />,
  attention: <ExclamationCircle />,
  warning: <ExclamationCircle />,
  critical: <ExclamationCircle />,
  magic: <InformationCircle />,
}

/** Inline message. Use for things the person must know on this screen. role=alert for critical. */
export function Banner({ tone = "info", title, children, action, onDismiss }: { tone?: Tone; title?: ReactNode; children?: ReactNode; action?: ReactNode; onDismiss?: () => void }) {
  return (
    <div role={tone === "critical" ? "alert" : "status"} className="flex items-start gap-x-3 rounded-[var(--k-r-md)] px-4 py-3" style={{ ...toneVars(tone), boxShadow: "inset 0 0 0 1px rgba(11,26,36,0.06)" }}>
      <span className="mt-0.5 shrink-0" aria-hidden>{BANNER_ICON[tone]}</span>
      <div className="min-w-0 flex-1 text-[13px]">
        {title && <div className="font-semibold">{title}</div>}
        {children && <div className={title ? "mt-0.5 opacity-90" : ""}>{children}</div>}
        {action && <div className="mt-2 font-medium">{action}</div>}
      </div>
      {onDismiss && (
        <button type="button" aria-label="Dismiss" onClick={onDismiss} className="-mr-1 rounded-md p-1 hover:bg-[color:var(--k-overlay-hover)]">
          <XMark />
        </button>
      )}
    </div>
  )
}

/**
 * KPI: eyebrow label, mono metric, change vs previous period. Whole tile can link.
 * `strip`: one cell of a <KpiStrip> (Keel's sunken KPI strip); `headline`: the strip's headline metric (keel-blue).
 */
export function Stat({ label, value, change, to, hint, strip, headline }: { label: string; value: ReactNode; change?: number | null; to?: string; hint?: string; strip?: boolean; headline?: boolean }) {
  const body = (
    <>
      <div className="k-eyebrow truncate">{label}</div>
      <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2">
        <span className="k-metric">{value}</span>
        {change != null && <Delta pct={change} />}
      </div>
      {hint && <div className="mt-1 text-[13px] text-[var(--tb-ink-3)]">{hint}</div>}
    </>
  )
  if (strip) {
    return to ? (
      <Link to={to} className="k-kpi" data-headline={headline || undefined}>{body}</Link>
    ) : (
      <div className="k-kpi" data-headline={headline || undefined}>{body}</div>
    )
  }
  const cls = "tf-card block px-4 py-3.5 transition-shadow"
  return to ? <Link to={to} className={cls + " hover:shadow-[var(--tb-shadow-pop)]"}>{body}</Link> : <div className={cls}>{body}</div>
}

/** Keel KPI strip: one surface-sunken panel holding the view's top metrics (pass <Stat strip />). */
export function KpiStrip({ children, label = "Key figures" }: { children: ReactNode; label?: string }) {
  return (
    <section aria-label={label} className="k-kpi-strip">
      {children}
    </section>
  )
}

export function Delta({ pct }: { pct: number }) {
  const up = pct >= 0
  return (
    <span className="inline-flex items-center rounded-[6px] px-1 font-[family-name:var(--k-font-mono)] text-[13px] font-medium" style={{ background: up ? "var(--tb-success-bg)" : "var(--tb-critical-bg)", color: up ? "var(--tb-success-fg)" : "var(--tb-critical-fg)" }}>
      {up ? <ArrowUpMini /> : <ArrowDownMini />}
      <span className="sr-only">{up ? "up " : "down "}</span>
      {Math.abs(pct).toFixed(0)}%
    </span>
  )
}

/** Toolbar row for filters, directly under a card's tabs. */
export function FilterBar({ children, label = "Filters" }: { children: ReactNode; label?: string }) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap items-center gap-2 border-t border-[var(--tb-line)] px-4 py-2.5">
      {children}
    </div>
  )
}

/** A filter shown as "Label  value ▾". Native select underneath, so it is keyboard and screen-reader perfect and fast on phones. */
export function FilterSelect({ label, value, onChange, options, allowAll = true }: { label: string; value: string; onChange: (v: string) => void; options: [string, string][]; allowAll?: boolean }) {
  const active = value !== "" && allowAll
  return (
    <label
      className="relative inline-flex h-8 items-center rounded-[8px] text-[13px] transition-colors focus-within:shadow-[0_0_0_2px_var(--tb-focus)]"
      style={{ background: active ? "var(--tb-info-bg)" : "var(--tb-surface)", boxShadow: active ? "0 0 0 1px var(--k-info-line)" : "0 0 0 1px var(--tb-line-strong)" }}
    >
      <span className="pl-2.5 pr-1 font-medium text-[var(--tb-ink-3)]">{label}</span>
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-bare className="tb-select-bare cursor-pointer font-medium text-[var(--tb-ink)] [field-sizing:content]"
      >
        {allowAll && <option value="">All</option>}
        {options.map(([v, l]) => (
          <option key={v} value={v}>{l}</option>
        ))}
      </select>
    </label>
  )
}

/** "From [date] To [date]" pair that reads as one control. */
export function DateRange({ from, to, onChange }: { from: string; to: string; onChange: (patch: { from?: string; to?: string }) => void }) {
  return (
    <div className="flex w-full items-center gap-x-1.5 text-[13px] font-medium text-[var(--tb-ink-3)] sm:inline-flex sm:w-auto">
      <span className="hidden sm:inline">Date</span>
      <input type="date" aria-label="From date" value={from} max={to || undefined} onChange={(e) => onChange({ from: e.target.value })} className="!h-8 min-w-0 flex-1 sm:w-[136px] sm:flex-none" />
      <span aria-hidden>–</span>
      <input type="date" aria-label="To date" value={to} min={from || undefined} onChange={(e) => onChange({ to: e.target.value })} className="!h-8 min-w-0 flex-1 sm:w-[136px] sm:flex-none" />
    </div>
  )
}

/** Shopify-style segmented tabs with counts. */
export function Tabs({ tabs, value, onChange, label = "Views" }: { tabs: { id: string; label: string; count?: number | null }[]; value: string; onChange: (id: string) => void; label?: string }) {
  return (
    <div role="tablist" aria-label={label} className="flex gap-x-1 overflow-x-auto px-3 py-2">
      {tabs.map((t) => {
        const on = t.id === value
        return (
          <button
            key={t.id}
            role="tab"
            aria-selected={on}
            type="button"
            onClick={() => onChange(t.id)}
            className="inline-flex h-8 shrink-0 items-center gap-x-1.5 rounded-[var(--k-r-sm)] px-3 text-[13px] font-medium transition-colors"
            style={{ background: on ? "var(--k-blue-50)" : "transparent", color: on ? "var(--k-blue)" : "var(--k-ink-muted)" }}
          >
            {t.label}
            {t.count != null && <span className="rounded-[4px] px-1.5 text-[11px] tabular-nums" style={{ background: on ? "var(--k-surface)" : "var(--k-sunken)" }}>{t.count}</span>}
          </button>
        )
      })}
    </div>
  )
}

/** A value that reads as a figure: "$1,204.50", "-$5.00", "12", "3.4%", "Nov 4, 2026". */
const isFigure = (v: ReactNode) => typeof v === "number" || (typeof v === "string" && (/^[\s$€£₹+\-−]*[\d.,]+\s*%?$/.test(v) || /^[A-Z][a-z]{2} \d{1,2}, \d{4}/.test(v)))

/** Key/value rows inside a card (order summary, totals, details). */
export function DefinitionList({ rows }: { rows: [ReactNode, ReactNode, boolean?][] }) {
  return (
    <dl className="flex flex-col gap-y-2 px-4 py-3 text-[13px]">
      {rows.map(([k, v, strong], i) => (
        <div key={i} className={"flex items-baseline justify-between gap-x-4 " + (strong ? "border-t border-[var(--tb-line)] pt-2 font-semibold" : "")}>
          <dt className={strong ? "" : "text-[var(--tb-ink-3)]"}>{k}</dt>
          {/* Keel: figures (money, counts, dates) in mono; words stay in Inter. */}
          <dd className={"text-right" + (isFigure(v) ? " k-num" : "")}>{v}</dd>
        </div>
      ))}
    </dl>
  )
}

/** Card of loading placeholders. */
export function LoadingCard({ lines = 3 }: { lines?: number }) {
  return (
    <div className="tf-card flex flex-col gap-y-3 p-4" aria-busy="true">
      {Array.from({ length: lines }).map((_, i) => <Skeleton key={i} className="h-4" style={{ width: `${90 - i * 18}%` }} />)}
    </div>
  )
}

/** Visually-hidden text for screen readers. */
export const SrOnly = ({ children }: { children: ReactNode }) => <span className="sr-only">{children}</span>
